import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import OnboardingExpEntryPoint, {
	type OnboardingExpEntryPointProps,
} from '@databox/ember-integrations/lib/components/OnboardingExp/OnboardingExpEntryPoint';

import type { ReactIntegrations, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getShadowDomMode } from 'webapp/utils/common';
import { mountReactRoot, unmountReactRoot } from 'webapp/utils/react-mount';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

type OnboardingExpResult = Parameters<OnboardingExpEntryPointProps['onComplete']>[0];
type OnboardingExpAnswer = OnboardingExpResult['answers'][number];

// Host-side copy of the script's questions and options (`onboarding-exp.script.ts`), needed to turn
// an answer's `optionNumber` back into a value worth persisting. `optionNumber` is a 1-based index
// into a question's `options`, stable regardless of the shuffled display order.

// Question 1 ("Which best describes your business?").
const ACCOUNT_TYPE_QUESTION_ID = 'account-type';
const STANDARD_OPTION_ID = 1;
const AGENCY_OPTION_NUMBER = 2;
const PORTFOLIO_OPTION_NUMBER = 3;

// Question 2 ("What's your main goal with Databox?"); a skipped question 1 falls back to standard.
const GOAL_QUESTION_ID = 'goal';
const GOAL_OPTION_LABELS_BY_ACCOUNT_TYPE: Record<number, string[]> = {
	[STANDARD_OPTION_ID]: [
		"Understand what's driving my numbers",
		'Stop building reports by hand',
		'Talk to my data and get answers fast',
		'Keep leadership informed',
		'Work with my data in ChatGPT or Claude',
	],
	[AGENCY_OPTION_NUMBER]: [
		'Spend less time on client reporting',
		'Show clients the impact of my work',
		"Talk to any client's data and get answers fast",
		'Keep an eye on every client at once',
		'Work with client data in ChatGPT or Claude',
	],
	[PORTFOLIO_OPTION_NUMBER]: [
		"Know who's ahead or behind, and why",
		'Stop chasing reports from everyone',
		'Talk to data across the whole group',
		"Keep an eye on everything and act before it's too late",
		'Work with my data in ChatGPT or Claude',
	],
};

// Question 3 ("What is your company size?"); options line up 1:1, in order, with `CompanySize` in
// onboarding/steps/account-setup.
const COMPANY_SIZE_QUESTION_ID = 'company-size';
const COMPANY_SIZE_OPTIONS = ['1-10', '11-50', '51-250', '251-1K', '1K-5K'];

// Question 4 ("Which tools do you want to pull data from? Type any others below."). Each chip
// stands for a broad category, not one integration — it maps to every source type key the category
// covers (per product spec); `otherText` (freely typed tools) has no such mapping and is dropped.
const TOOLS_QUESTION_ID = 'tools';
const TOOLS_OPTION_SOURCE_TYPE_KEYS = [
	['GoogleAnalytics4'],
	['Hubspot', 'HubspotMarketing', 'HubspotServices'],
	['GoogleSheets', 'Excel'],
	['PostgreSQL', 'MySQL', 'MSSQL', 'BigQuery', 'Snowflake', 'AzureSQL', 'AmazonRedshift'],
];

function findAnswer(
	answers: OnboardingExpAnswer[],
	questionId: string,
): OnboardingExpAnswer | undefined {
	return answers.find((answer) => answer.questionId === questionId);
}

// The picked option's label, combined with any typed free text ("<option>, <free text>") the same
// way the card's own summary does, or the free text alone when no option was picked.
function answerText(
	answer: OnboardingExpAnswer | undefined,
	optionLabels: string[],
): string | undefined {
	if (!answer || answer.skipped) {
		return undefined;
	}

	const optionLabel =
		answer.optionNumber !== null ? optionLabels[answer.optionNumber - 1] : undefined;
	if (optionLabel && answer.otherText) {
		return `${optionLabel}, ${answer.otherText}`;
	}

	return optionLabel || answer.otherText || undefined;
}

export default class OnboardingStepsGenieExpComponent extends Component {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('experiment/genie-onboarding-920261')
	declare experimentGenieOnboarding920261Service: Service.Experiment.GenieOnboarding920261;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('onboarding') declare onboardingService: Service.Onboarding;
	@service('react') declare reactService: Service.React;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	reactElement: ReactIntegrations.Root = null;
	private isFinished = false;
	private isFlowB = false;
	private isFlowC = false;
	private isSkillFlow = false;

	@action
	setupView(node: HTMLElement) {
		this.isSkillFlow = this.onboardingService.isEligibleForMarketplaceSkillFlow;

		const { isEligibleForFlowB, isEligibleForFlowC } =
			this.experimentGenieOnboarding920261Service;
		this.isFlowB = isEligibleForFlowB;
		this.isFlowC = isEligibleForFlowC;

		if (this.isSkillFlow || this.isFlowC || this.isFlowB) {
			this.analyticsService.event(TrackingEvent.ONBOARDING_START, {
				experiments: [this.experimentGenieOnboarding920261Service.experimentOrigin],
			});

			this.analyticsService.properties(
				{ experiments: this.experimentGenieOnboarding920261Service.experimentOrigin },
				['user', 'company'],
			);
		}

		// The React layout uses height: 100%; give it a definite full-viewport height.
		node.style.height = '100dvh';
		mountReactRoot(this, node, () => this.renderExperiment());
	}

	@action
	teardownView() {
		unmountReactRoot(this);
	}

	@action
	handleComplete(result: OnboardingExpResult) {
		this.finish(result);
	}

	@action
	handleSkip(result: OnboardingExpResult) {
		this.finish(result);
	}

	private finish(result: OnboardingExpResult) {
		if (this.isFinished) {
			return;
		}
		this.isFinished = true;

		if (this.isSkillFlow) {
			// Only attribution and account type were asked; the final step installs the skills.
			this.onboardingService.nextStep();
			this.trackAttribution(result.attribution);
			this.saveAccountType(result.answers);
			return;
		}

		const { websitePrompt } = this.experimentGenieOnboarding920261Service;
		if (this.isFlowC && websitePrompt) {
			this.finishFlowC(result, websitePrompt);
			return;
		}

		if (!this.isFlowB) {
			// Flow A: only the attribution question was asked. Advance onboarding instead of
			// completing it — Account setup / Integrations / Final still need to run.
			//
			// Compute "next" before trackAttribution() below marks isHomeAttributionQuestionClosed:
			// OnboardingService#steps depends on it, and Account setup's own employees save (fired
			// right before this step, not awaited) may still be in flight. Advancing first — while
			// this step is still genuinely present in the steps array — keeps the transition correct
			// regardless of whether Account setup's save has landed yet; advancing after would let
			// Account setup reappear once this step drops out of a freshly recomputed steps array.
			this.onboardingService.nextStep();
			this.trackAttribution(result.attribution);
			return;
		}

		this.trackAttribution(result.attribution);

		const accountType = findAnswer(result.answers, ACCOUNT_TYPE_QUESTION_ID)?.optionNumber;
		const goalOptionLabels =
			GOAL_OPTION_LABELS_BY_ACCOUNT_TYPE[accountType ?? STANDARD_OPTION_ID] ?? [];
		const goal = answerText(findAnswer(result.answers, GOAL_QUESTION_ID), goalOptionLabels);
		const companySizeAnswer = findAnswer(result.answers, COMPANY_SIZE_QUESTION_ID);
		const employees =
			companySizeAnswer?.optionNumber !== undefined && companySizeAnswer.optionNumber !== null
				? COMPANY_SIZE_OPTIONS[companySizeAnswer.optionNumber - 1]
				: undefined;
		const toolsAnswer = findAnswer(result.answers, TOOLS_QUESTION_ID);
		const sourcesSelection =
			toolsAnswer?.optionNumber !== undefined && toolsAnswer.optionNumber !== null
				? TOOLS_OPTION_SOURCE_TYPE_KEYS[toolsAnswer.optionNumber - 1]
				: undefined;
		// Free-typed tools ("otherText") don't map to a SourceType key, so they can't join
		// sourcesSelection (metadata consumed by real key-matching logic elsewhere) — but they should
		// still reach tracking instead of being dropped when no chip was picked.
		const cloudSourcesSelectionCompany =
			sourcesSelection ?? (toolsAnswer?.otherText ? [toolsAnswer.otherText] : undefined);

		if (goal) {
			this.analyticsService.properties({ trial_objective: goal }, ['user', 'company']);
		}
		if (cloudSourcesSelectionCompany) {
			this.analyticsService.properties(
				{ cloud_sources_selection_company: cloudSourcesSelectionCompany },
				['user', 'company'],
			);
		}

		void this.accountService.saveMetadataProperties({
			...(goal ? { goal } : {}),
			...(employees ? { employees } : {}),
			...(sourcesSelection ? { sourcesSelection } : {}),
		});

		this.saveAccountType(result.answers);

		this.onboardingService.markCompleted();
		this.aiChatService.openGenieWithOnboardingAnswers(result.prompt);
	}

	// Flow C (920261_C): the visitor picked a prompt on the marketing site (cookie exp_920262_prompt),
	// so the goal / trial_objective come from that instead of a wizard question, and the wizard only
	// asked attribution + account type.
	private finishFlowC(result: OnboardingExpResult, goal: string): void {
		this.trackAttribution(result.attribution);
		this.saveAccountType(result.answers);

		void this.accountService.saveMetadataProperties({ goal });
		this.analyticsService.properties({ trial_objective: goal }, ['user', 'company']);
		this.analyticsService.properties({ is_onboarded_metadata: true }, ['user', 'company']);

		this.onboardingService.markCompleted();
		this.experimentGenieOnboarding920261Service.clearWebsitePrompt();

		const prompt = `I'm new here — here is how you can help me:\n${goal}\n\nAsk me a few questions to understand my needs, then guide me from here.`;
		this.aiChatService.openGenieWithOnboardingAnswers(prompt, false);
	}

	// Same constructs as the Home attribution banner (home/attribution-question.gts): track it the
	// same way, and close it out via the same user setting, which also doubles as the gate
	// OnboardingService#steps uses to not show this step again.
	private trackAttribution(attribution: string | null): void {
		if (!attribution) {
			return;
		}

		this.analyticsService.event(TrackingEvent.ONBOARDING_ACCOUNT_DETAILS);
		this.analyticsService.properties({ signup_referral: attribution }, ['user', 'company']);
		this.userSettingsService.isHomeAttributionQuestionClosed = true;
	}

	// An agency or portfolio answer turns the space into an agency one (portfolio also flags it).
	private saveAccountType(answers: OnboardingExpAnswer[]): void {
		const accountType = findAnswer(answers, ACCOUNT_TYPE_QUESTION_ID);
		const isAgency = accountType?.optionNumber === AGENCY_OPTION_NUMBER;
		const isPortfolio = accountType?.optionNumber === PORTFOLIO_OPTION_NUMBER;
		if ((isAgency || isPortfolio) && this.accountService.isEligibleForAccountTypeChoice) {
			void this.accountService.saveSpaceAsAgency(isPortfolio);
		}
	}

	private renderExperiment() {
		if (!this.reactElement) {
			return;
		}

		const userId = this.currentUserService.model?.id;
		const props: OnboardingExpEntryPointProps = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			user: {
				id: userId ? Number(userId) : null,
				name: this.currentUserService.model?.firstName ?? '',
			},
			showAttribution: true,
			showAccountType: this.isSkillFlow || this.isFlowC || this.isFlowB,
			showMetadataQuestions: !this.isFlowC && this.isFlowB,
			onComplete: this.handleComplete,
			onSkip: this.handleSkip,
		};

		this.reactService.render(
			this.reactElement,
			OnboardingExpEntryPoint(props),
			getShadowDomMode(this.accountService.email),
		);
	}

	<template>
		<div
			class="onboarding-genie-exp"
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		></div>
	</template>
}
